import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest, downloadApiFile } from '../../utils';
import PlanGrid, { runLabel } from '../../components/common/PlanGrid';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyTournament.css';

/* What kind of competition this event is, and how big the day is.

   ★ An event is not a competition until this screen says so. That is one row, and no row means
   the event stays exactly the ordinary event it has always been — which is what makes it safe to
   put this in front of a platform running live events.

   ★★ Two questions, and keeping them apart is the whole design. WHAT competes (teams or people)
   and HOW it is played (everyone in every game, a rotation, a knockout) have nothing to do with
   each other: a format is a schedule plus a ranking, and neither cares whether the thing being
   scheduled is a team or a person.

   ★★ This screen is the COCKPIT, and it breaks out rather than growing. There is a lot to decide
   about a competition — the field, the games, the schedule — and a page that holds all of it is a
   page nobody can see the top of. So: what it is, how big it is, and one line each for the parts
   that have their own screen.

   ★ Compact, but still teaching. The options are chips and only the CHOSEN one prints its
   sentence; the rest are one click away under "what do these mean". A company setting up its
   first tournament does not have the words — heat, bracket, seeding, stage — and the answer to
   that is to keep the explanations, not to keep them all on screen at once.

   ★ The numbers are the same event columns the back office edits. That is deliberate: the two
   must never hold different answers to "how many teams are playing".

   ★ Three kinds of "not yet", and they read differently on purpose:
       a MODE that cannot be chosen    the data model cannot hold it yet — refused by the server.
       a FORMAT marked "soon"          it is named and explained, but it cannot be measured yet,
                                       so it cannot be picked either — also refused by the server.
       a FORMAT with no generator      choose it now; the schedule for it is built later.

   ★★ The formats that cannot be picked are still on the screen, with "soon" on them. A picker
   holding three options says "this is all a competition can be"; the same picker holding eight
   says what a competition can be and which of those we are still building — and it is the one
   place a customer can tell us the one they actually wanted before we build the wrong one. */

// ★ Only icon names already proven elsewhere in the app. A Flaticon name that does not exist
// renders as an empty box rather than failing the build, so a "nice" guess is a silent bug.
const FORMAT_ICONS = {
  heats: 'list-check',
  rotation: 'refresh',
  knockout: 'trophy',
  pools: 'users-alt',
  round_robin: 'leaderboard',
  swiss: 'balance-scale-right',
  ladder: 'ranking-star',
  double_knockout: 'redo',
};
const MODE_ICONS = { teams: 'users', individual: 'user' };

// The two blocks of numbers, in the order Machina's Dimensions screen asks them — an organiser
// who has seen one screen should recognise the other.
const SIZE_FIELDS = ['amount_of_players', 'amount_of_teams', 'players_per_team',
  'amount_of_challenges', 'amount_of_groups', 'amount_of_stations', 'teams_per_challenge'];
const TIME_FIELDS = ['registration_time', 'challenge_time', 'challenge_break_time',
  'extra_break_time', 'award_time'];

/* ★★ The calculator. Two of the boxes in "The field" can be worked out from the others, the same
   two Machina's Dimensions screen has always computed:

       players per team      = ceil(players / teams)
       teams playing at once = ceil(teams / (games × groups × courts))

   ★★ On a BUTTON, not live. Machina recalculates the moment a driver changes, which means a
   number you typed is silently replaced while you are still filling the form in — you look up and
   your answer is gone. Here the box is yours and stays yours; "Calculate" fills it in when you
   ask. The organiser knows their venue better than the arithmetic does, and the arithmetic is a
   starting point, not a correction.

   ★ The formula itself is NOT repeated here. The value comes back from the preview route, the
   same one that says when the day ends, so there is one implementation and not two that drift.
   This map only says which box follows which fields — used for the button and for its hint. */
const DERIVED_FROM = {
  players_per_team: ['amount_of_players', 'amount_of_teams'],
  teams_per_challenge: ['amount_of_teams', 'amount_of_challenges',
    'amount_of_groups', 'amount_of_stations'],
};
// What the preview calls each of them.
const PREVIEW_KEY = { players_per_team: 'players_per_team',
  teams_per_challenge: 'teams_per_heat' };

const isBlank = (value) => value === null || value === undefined || value === '';

/* ★★ What the box was last worked out FROM, as one string. A Calculate button that looks the
   same whether or not it still applies is a button an organiser stops reading: they press it
   once, change the number of teams three fields later, and the answer above is quietly stale —
   which is precisely the failure the live recalculation was removed to avoid, only slower.

   So the drivers are remembered at the moment the box was filled (loaded, saved, or calculated)
   and the button lights up as soon as they no longer match. It still never writes on its own. */
const driverSignature = (field, source) => (DERIVED_FROM[field] || [])
  .map((f) => (isBlank((source || {})[f]) ? '' : String((source || {})[f]))).join('|');

const driverBaseline = (source) => Object.fromEntries(
  Object.keys(DERIVED_FROM).map((field) => [field, driverSignature(field, source)]));

const CompanyEventTournament = () => {
  // ★ `i18n.language` rides along on the print link: the PDF is rendered server-side and looks
  //   its words up in this very locale file, so the sheet comes out in the organiser's language.
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [dims, setDims] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [explain, setExplain] = useState(false);
  const [runSheet, setRunSheet] = useState(false);
  const [calcBase, setCalcBase] = useState({});
  const [notice, setNotice] = useState(null);
  // De ladder terwijl ze getypt wordt. Los van ``data`` gehouden om dezelfde reden als de maten:
  // een getal dat iemand aan het invullen is, is nog geen beslissing.
  const [ladderRows, setLadderRows] = useState([]);
  const [ladderDirty, setLadderDirty] = useState(false);
  const timer = useRef(null);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/tournament`)
      .then((res) => {
        setData(res);
        setDims(res.dimensions || {});
        setCalcBase(driverBaseline(res.dimensions || {}));
        setPreview(res.preview || null);
        setLadderRows((res.ladder || {}).rows || []);
        setLadderDirty(false);
        setDirty(false);
      })
      // ★ REPLACE, never push: a screen that bounces on load must not stay in the history behind
      // the one it bounced to, or stepping back lands on it and it bounces again.
      .catch(() => navigate('/programmes', { replace: true }));
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  const save = (fields) => {
    setBusy(true);
    setNotice(null);
    apiRequest('PUT', `/company/event/${eventId}/tournament`, fields)
      .then((res) => {
        setData((d) => ({ ...d, ...res }));
        if (res.dimensions) {
          setDims(res.dimensions);
          setCalcBase(driverBaseline(res.dimensions));
        }
        if (res.preview) setPreview(res.preview);
        setDirty(false);
      })
      .catch((e) => setNotice(apiErrorText(t, e)))
      .finally(() => setBusy(false));
  };

  const stop = () => {
    setBusy(true);
    setNotice(null);
    apiRequest('DELETE', `/company/event/${eventId}/tournament`)
      .then(() => setData((d) => ({ ...d, stage: null })))
      .catch((e) => setNotice(apiErrorText(t, e)))
      .finally(() => setBusy(false));
  };

  /* Running one of the day's set-up steps. Each answers with the block it changed, so the screen
     re-adopts that and leaves the rest of a long page alone. */
  const step = (method, path, body) => {
    setBusy(true);
    setNotice(null);
    return apiRequest(method, `/company/event/${eventId}/tournament${path}`, body)
      .then((res) => {
        setData((d) => ({ ...d, ...res }));
        // Elke stap die de ladder aanraakt antwoordt met de ladder; dan is de tabel eronder de
        // ladder die er staat, niet die er stond.
        if (res.ladder) { setLadderRows(res.ladder.rows || []); setLadderDirty(false); }
        return res;
      })
      .catch((e) => { setNotice(apiErrorText(t, e)); throw e; })
      .finally(() => setBusy(false));
  };

  /* ★★ De ladder wegschrijven — en zeggen wat er daardoor herrekend is. ``Score.score`` is
     gematerialiseerd op het moment van scoren, dus een ladder die verandert nadat er gespeeld is,
     herschrijft uitslagen die al meegedeeld zijn. Dat mag, maar het mag niet stil. */
  const saveLadder = () => {
    step('PUT', '/ladder', { rows: ladderRows })
      .then((res) => setNotice(res.restated
        ? t('tournamentLadderRestated', { n: res.restated }) : t('saved')))
      .catch(() => {});
  };

  const setLadderCell = (id, field, value) => {
    setLadderRows((rows) => rows.map((row) => (row.id === id
      ? { ...row, [field]: value === '' ? '' : Math.max(0, Math.min(999, Number(value))) }
      : row)));
    setLadderDirty(true);
  };

  /* ★★ The computed day, written onto the real run sheet — once, as a starting point.
     The rows come from THIS screen rather than from the server precisely because the words are
     here: `runLabel` reads them out of the translation files in the organiser's own language, and
     a run sheet seeded from the server would be in English on a Dutch day. What the server owns is
     which lines are playing moments, and that it happens only once. */
  const seedRunSheet = () => {
    const day = (data.days || {}).first || ((data.event || {}).date || '').slice(0, 10);
    const rows = (preview.schedule || [])
      .filter((row) => row.start)
      .map((row) => ({
        title: runLabel(t, row, breakFollowsGame),
        start: `${day}T${row.start}`,
        end: `${day}T${row.end || row.start}`,
        // A 'game' row on a rotation, a 'block' on a knockout or a round robin — both are when the
        // competition is PLAYED, and everything else on the sheet is the programme around it.
        is_challenge: row.kind === 'game' || row.kind === 'block',
      }));
    if (!rows.length) { setNotice(t('tournamentRunNoStart')); return; }
    step('POST', '/runsheet', { rows })
      .then(() => load())
      .catch(() => {});
  };

  /* ★ The read-out is computed on the SERVER, debounced, and writes nothing. Typing is not
     deciding, so a keystroke must not reach the event's columns — and recomputing the day's
     length in JavaScript would be a second implementation of it that drifts from the first. */
  const askPreview = useCallback((next, fmt) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      apiRequest('POST', `/company/event/${eventId}/tournament/preview`,
        { dimensions: next, format: fmt })
        .then((res) => setPreview(res.preview))
        .catch(() => {});
    }, 400);
  }, [eventId]);

  /* ★ The one thing that writes into a box the organiser is filling in, and it only ever runs
     because they pressed it. Immediate, not debounced: a button that answers in 400ms reads as a
     button that did not work. */
  const calculate = (field) => {
    setBusy(true);
    apiRequest('POST', `/company/event/${eventId}/tournament/preview`,
      { dimensions: dims, format: stage.format })
      .then((res) => {
        setPreview(res.preview);
        const value = res.preview[PREVIEW_KEY[field]];
        if (!value) { setNotice(t('dim_cannotCalculate')); return; }
        setDims((current) => ({ ...current, [field]: value }));
        // Answered for THESE drivers — the button goes quiet until one of them moves again.
        setCalcBase((base) => ({ ...base, [field]: driverSignature(field, dims) }));
        setDirty(true);
      })
      .catch((e) => setNotice(apiErrorText(t, e)))
      .finally(() => setBusy(false));
  };

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // ★ With the arrow, so a slow read is never a screen somebody is stuck on.
  if (!data) {
    return (
      <Base activeNav="home">
        <Card><Back to={`/company-event/${eventId}/edit`} /></Card>
      </Base>
    );
  }

  const { stage, vocabulary, can_manage: canManage, teams, games } = data;
  // What the day is BUILT from, against what the numbers say it needs, plus the reasons the
  // schedule cannot be generated yet. All three come from the server — see `_schedule_blocked`.
  const sched = data.schedule || { rows: 0, moments: 0, needed: 0, heats: 0 };
  const ladder = data.ladder || { places: 0, top: null };
  // De joker als toestand van de dag — null op een event dat er niets mee doet.
  const joker = data.joker || null;
  // Houses, when the company runs them. null when it does not, so a day played without
  // them draws nothing rather than an empty block reading zero.
  const houses = data.houses || null;
  // Een joker die nul waard is, is een vinkje dat niets doet. Gelezen van wat er in de tabel
  // staat, niet van wat er bewaard is: wie de nullen net getypt heeft, moet het meteen zien.
  const jokerPaysNothing = ladderRows.length > 0
    && ladderRows.every((row) => !Number(row.joker));
  const blocked = data.schedule_blocked || [];
  // One day unless the event's own dates say otherwise — worked out server-side, in the event's
  // timezone, because a browser subtracting two UTC stamps gets a different answer.
  const days = data.days || { count: 1 };
  // How many people the company has on its roster — the same number the Members tile shows.
  const members = (data.members || {}).count || 0;
  const locked = !canManage || busy;
  const formatInfo = (vocabulary.formats || []).find((f) => f.key === stage?.format) || {};
  /* ★★ Read off the FORMAT, never off its name. This screen tested `format === 'knockout'` in six
     places, and those six were part of the seven the platform's single play formula used to be
     smeared over — the reason a second formula could never be added without touching all of them.
     The spec already answers every one of these questions, so a format added after this costs this
     screen nothing at all. `isKnockout` survives for the one block that IS about knockouts and
     nothing else: seeding and the small final. */
  const oneGame = formatInfo.fixed_challenges === 1;
  const noGroups = formatInfo.has_groups === false;
  const usesLegs = !!formatInfo.uses_legs;
  /* ★★ Which stage settings this format actually reads, in the order to ask them — off the spec,
     not off the format's name. `pools` needs four of them and a knockout two, and the block below
     renders whichever it is handed; a format added after this puts its questions on this screen
     by declaring them in its own module. */
  const stageFields = formatInfo.stage_fields || [];
  /* ★★ Does THIS screen's "build the schedule" button build this format? The platform has two
     engines: 'planning' (a heat of N, everybody gets a place — what Hercules Trophy has always
     played) and 'match' (a heat of two, one wins), and the second builds itself on the bracket
     screen out of its own fixtures.
     ⚠️ Offering both doors was not merely untidy: pressing Generate on a drawn bracket ran the
     rotation builder, whose first line wipes every heat, every competitor and every score of the
     event — leaving a tree pointing at rows that no longer exist. */
  const buildsHere = formatInfo.engine === 'planning';
  /* ★ Ranked on a TABLE rather than on a tree — the other half of what a format is, off the spec
     and not off its name. It only decides the words on the door below; the screen behind it
     renders itself off the same fact. */
  const isTable = formatInfo.ranked_by === 'table';
  const isKnockout = stage?.format === 'knockout';
  // ★ What only a pools day can say — the pools, their size, the round that follows. A fact from
  // the preview rather than a name test, same rule as `matches != null` below.
  const poolShape = preview?.detail?.pools ? preview.detail : null;

  const setField = (field, value) => {
    const next = { ...dims, [field]: value };
    setDims(next);
    setDirty(true);
    // Refreshes the READ-OUT only — the line under the grid and the end time. No box a person is
    // typing in is ever written to by this.
    askPreview(next, stage.format);
  };

  // ★ Two groups or more and a group's break IS the other group's game, so the break is not the
  // organiser's to type. Greyed with the number in it and a line saying why.
  const breakFollowsGame = !!preview?.break_follows_game;

  // A field the format or the set-up has no use for is drawn greyed rather than removed: an
  // organiser who switches to a knockout should see WHY the groups box went quiet.
  const disabledField = (field) => {
    if (noGroups && field === 'amount_of_groups') return true;
    if (oneGame && field === 'amount_of_challenges') return true;
    return field === 'challenge_break_time' && breakFollowsGame;
  };

  /* ★★ Does this box still follow from the numbers above it? Three answers, and they are not the
     same thing:
        a driver is still blank   nothing to work out — the button stays quiet.
        the box itself is blank   there is an answer waiting to be asked for: light it up.
        a driver has MOVED        the number in the box was worked out from something that is no
                                  longer true. It is still the organiser's number, so it is not
                                  overwritten — but the screen has to say so, or the stale answer
                                  survives all the way to the run sheet. */
  const calcStale = (field) => {
    if (!DERIVED_FROM[field] || disabledField(field)) return false;
    if (DERIVED_FROM[field].some((f) => isBlank(dims[f]))) return false;
    if (isBlank(dims[field])) return true;
    return calcBase[field] !== driverSignature(field, dims);
  };

  // A forced box shows the number the schedule will actually use, not the one that happens to be
  // stored — a greyed box holding a stale value is worse than no box at all.
  const forcedValue = (field) => {
    if (field === 'challenge_break_time' && breakFollowsGame) {
      return preview.challenge_break_time == null ? '' : preview.challenge_break_time;
    }
    return dims[field] == null ? '' : dims[field];
  };


  /* ★★ "…and that is what share of us?" — the question every organiser asks straight after
     typing how many people are playing, and the one they otherwise answer by opening the Members
     tile in a second tab and doing the sum in their head.

     ★ Read-only, like "Ends at": it is an answer, not an input. The denominator is the roster
     count served by the API (``_roster_totals``, the same number the Members tile shows), so
     this screen and that tile can never say two different things about the company's size.

     ★ Over 100% is not an error and is not corrected — a company can bring guests, a partner, a
     supplier team. It is SAID, because the other reason for 140% is a number typed into the
     wrong box, and that is worth catching before the catering is ordered. */
  const memberShare = () => {
    const players = Number(dims.amount_of_players);
    const share = players > 0 ? Math.round((players / members) * 100) : null;
    return (
      <span key="ct-members-share" className="ct-field">
        <label htmlFor="ct-share">{t('dim_memberShare')}</label>
        <span className="ct-field__row">
          <input id="ct-share" type="text" readOnly disabled
                 value={share == null ? '' : `${share}%`} />
        </span>
        <span className={`ct-field__hint${share > 100 ? ' ct-field__hint--now' : ''}`}>
          {t(share > 100 ? 'dim_memberShareOver' : 'dim_memberShareHint', { members })}
        </span>
      </span>
    );
  };

  const num = (field, opts = {}) => {
    const stale = calcStale(field);
    return (
      <span key={field} className="ct-field">
        <label htmlFor={`ct-${field}`}>
          {t(oneGame && opts.matchLabel ? opts.matchLabel : `dim_${field}`)}
        </label>
        <span className="ct-field__row">
          <input
            id={`ct-${field}`}
            type={opts.type || 'number'}
            min={opts.min != null ? opts.min : 0}
            max={opts.max}
            step="1"
            value={forcedValue(field)}
            disabled={locked || disabledField(field)}
            onChange={(e) => setField(field, e.target.value === '' ? null : e.target.value)}
          />
          {/* ★ The unit beside the box, not inside the label. Every one of these is a NUMBER OF
              MINUTES and nothing on the screen said so — a "30" next to "Awards ceremony" reads as
              half an hour to whoever wrote it and as half past to whoever reads it next. */}
          {opts.unit && <span className="ct-field__unit">{t('dim_unitMinutes')}</span>}
          {/* Fills the box in when asked, and never on its own. */}
          {DERIVED_FROM[field] && (
            <button type="button" className={`ct-calc${stale ? ' ct-calc--stale' : ''}`}
                    disabled={locked || disabledField(field)}
                    onClick={() => calculate(field)}>
              {t('dim_calculate')}
            </button>
          )}
        </span>
        {/* The stale line REPLACES the "press Calculate" line rather than joining it: two hints
            under one box is a paragraph, and the urgent one is the one that changed. */}
        {stale && <span className="ct-field__hint ct-field__hint--now">{t('dim_calcStale')}</span>}
        {!stale && opts.hint && <span className="ct-field__hint">{t(opts.hint)}</span>}
      </span>
    );
  };

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/company-event/${eventId}/edit`} />
        <Title my="1" className="mt-3">
          <Icon name="flag-checkered" theme="primary" className="me-2" />
          {t('tournamentTitle')}
        </Title>

        {notice && <p className="ce-note ce-note--warn">{notice}</p>}

        {/* ---- NOT A COMPETITION (yet) ---- */}
        {!stage && (
          <>
            <p className="bravos-q__hint mb-2">{t('tournamentIntro')}</p>
            <p className="ce-note">{t('tournamentNotOne')}</p>
            {canManage && (
              <button type="button" className="btn btn-primary mt-2" disabled={busy}
                      onClick={() => save({})}>
                <Icon name="plus" className="me-1" />{t('tournamentMakeOne')}
              </button>
            )}
          </>
        )}

        {stage && (
          <>
            {/* ---- WHAT IS PLAYED ----
                First, and above the two picks, because it is the only thing on this screen that
                is about the SPORT rather than about the paperwork around it. Everything below
                arranges games — how they are played, who plays them, how many fit in an evening
                — and arranging games you have not chosen yet is arranging nothing. It is also
                what the schedule is literally built from: the generator reads the games ON the
                event, while every number in "how big the day is" was measured against how many
                games there are. When those two disagree the day comes out short and nothing else
                says so, which is why the gap is spelled out here rather than left to be
                discovered on the games screen.

                A door and one line about what is behind it — picking the games and writing their
                rules is a screen of its own; what belongs here is whether it has been done. */}
            {/* ★ The same label as "How it is played" and "What competes" below, and not a
                heading: `h1..h6` are set in Poppins (assets/styles/App.css) while a `p` is in the
                body stack, so an h3 here put the first of three questions about the same
                competition in a different TYPEFACE from the other two. Moving the games to the
                top is what made that visible. No icon either — the two labels it now matches have
                none, and `.fi` sets its own font-size, so a glyph beside 0.7rem small caps comes
                out bigger than the words. */}
            <p className="ct-pick__label mt-2">{t('gamesTitle')}</p>

            {/* ★ The pictures first, and the sentence under them. "What is played" answered with
                four photographs is read in the time it takes to look at it, and it is the one
                thing on this cockpit that means something to somebody who does not yet know what
                a heat or a stage is.

                ★ A game with NO picture keeps its place as an empty frame rather than being left
                out. A strip shorter than the day would quietly under-report what is on it — and
                the gap is the invitation: that frame is the thing you press to go and fill it. */}
            {games?.photos?.length > 0 && (
              <div className="ct-gamestrip">
                {games.photos.map((game) => (
                  <button
                    key={game.id}
                    type="button"
                    className="ct-gamestrip__one"
                    title={game.name}
                    aria-label={game.name}
                    onClick={() => navigate(`/company-event/${eventId}/games`)}
                  >
                    {game.photo_url
                      ? <img src={game.photo_url} alt="" loading="lazy" />
                      : <span className="ct-gamestrip__empty" title={t('gamesPhotoAdd')}>
                          <Icon name="picture" />
                        </span>}
                  </button>
                ))}
              </div>
            )}

            <p className="ct-sum">
              {games?.count
                ? t('tournamentGamesSummary', { count: games.count, needed: games.needed })
                : t('tournamentGamesEmpty')}
            </p>
            {games?.count > 0 && games.needed > 0 && games.count !== games.needed && (
              <p className="ce-note ce-note--warn">
                {t('tournamentGamesMismatch', { count: games.count, needed: games.needed })}
              </p>
            )}
            {games?.unscored > 0 && (
              <p className="ce-note ce-note--warn">
                {t('tournamentGamesUnscored', { n: games.unscored })}
              </p>
            )}
            <div className="ce-actions">
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => navigate(`/company-event/${eventId}/games`)}>
                <Icon name="running" /> {t('tournamentGoToGames')}
              </button>
            </div>

            {/* ---- HOUSES ----
                Only drawn once the company actually runs houses: a day played without them is
                every day that exists today, and an empty block reading "0 houses" would teach a
                customer that they are missing something they are not.

                ★ The two half-done states are what this says out loud, because both are silent
                on every other screen: a team with no house scores for nobody, and a house with no
                team can never score at all. */}
            {houses && (
              <>
                <p className="ct-pick__label mt-2">{t('housesTitle')}</p>
                <p className="ct-sum">
                  {t('tournamentHousesSummary', { count: houses.count, teams: houses.teams })}
                </p>
                {houses.unassigned > 0 && (
                  <p className="ce-note ce-note--warn">
                    {t('tournamentHousesUnassigned', { n: houses.unassigned })}
                  </p>
                )}
                {houses.empty > 0 && (
                  <p className="ce-note ce-note--warn">
                    {t('tournamentHousesEmpty', { n: houses.empty })}
                  </p>
                )}
                <div className="ce-actions">
                  <button type="button" className="bravos-submit bravos-submit--ghost"
                          onClick={() => navigate(`/company-event/${eventId}/houses`)}>
                    <Icon name="flag" /> {t('tournamentGoToHouses')}
                  </button>
                </div>
              </>
            )}

            {/* ---- HOW IT IS PLAYED · WHAT COMPETES ----
                Two chip rows. Only the chosen option prints its sentence; the rest are one
                click away, because there is a screenful of numbers under this.

                ★ The format is asked FIRST. It is the decision the whole screen under it hangs
                on — how many rounds there are, whether there are groups, how many games the day
                has to hold, whether a schedule can be built at all — while who is entered reads
                the same for a knockout as for a round robin. It is also the one that stops being
                a question: generating the schedule freezes the format, and the mode stays open
                after it. */}
            <div className="ct-pick">
              <p className="ct-pick__label">{t('tournamentFormat')}</p>
              <div className="ce-tabs">
                {vocabulary.formats.map((format) => (
                  <button
                    key={format.key}
                    type="button"
                    className={`ce-tab ${stage.format === format.key ? 'is-on' : ''}`}
                    disabled={locked || !stage.can_change_format || !format.available}
                    onClick={() => save({ format: format.key })}
                  >
                    <Icon name={FORMAT_ICONS[format.key]} />
                    {t(`tournamentFormat_${format.key}`)}
                    {/* Not hidden: the customer should know this exists and is coming. */}
                    {!format.available && <span className="ct-soon">{t('tournamentSoon')}</span>}
                  </button>
                ))}
              </div>
              <p className="ct-pick__note">{t(`tournamentFormatNote_${stage.format}`)}</p>
              {!stage.can_change_format && (
                <p className="ce-note">{t('tournamentFormatLocked')}</p>
              )}
              {!formatInfo.can_generate && (
                <p className="ct-field__hint">{t('tournamentNoScheduleYet')}</p>
              )}

              <p className="ct-pick__label mt-2">{t('tournamentWhoCompetes')}</p>
              <div className="ce-tabs">
                {vocabulary.modes.map((mode) => (
                  <button
                    key={mode.key}
                    type="button"
                    className={`ce-tab ${stage.mode === mode.key ? 'is-on' : ''}`}
                    disabled={locked || !mode.available}
                    onClick={() => save({ mode: mode.key })}
                  >
                    <Icon name={MODE_ICONS[mode.key]} />
                    {t(`tournamentMode_${mode.key}`)}
                    {/* Not hidden: the customer should know this exists and is coming. */}
                    {!mode.available && <span className="ct-soon">{t('tournamentSoon')}</span>}
                  </button>
                ))}
              </div>
              <p className="ct-pick__note">{t(`tournamentModeNote_${stage.mode}`)}</p>

              {/* ★ The teaching copy the first build argued for is all still here — it is just
                  not all on screen at once. */}
              <button type="button" className="ct-explain__toggle"
                      onClick={() => setExplain((v) => !v)}>
                <Icon name={explain ? 'angle-left' : 'angle-right'} className="me-1" />
                {t('tournamentWhatDoTheseMean')}
              </button>
              {explain && (
                <div className="ct-explain">
                  <p className="ct-pick__label">{t('tournamentFormat')}</p>
                  <p className="ct-field__hint">{t('tournamentFormatNote')}</p>
                  {vocabulary.formats.map((format) => (
                    <p key={format.key} className="ct-explain__row">
                      <b>{t(`tournamentFormat_${format.key}`)}</b>
                      {` — ${t(`tournamentFormatNote_${format.key}`)}`}
                      {!format.available && ` (${t('tournamentNotYetAvailable')})`}
                    </p>
                  ))}
                  <p className="ct-pick__label mt-2">{t('tournamentWhoCompetes')}</p>
                  <p className="ct-field__hint">{t('tournamentWhoCompetesNote')}</p>
                  {vocabulary.modes.map((mode) => (
                    <p key={mode.key} className="ct-explain__row">
                      <b>{t(`tournamentMode_${mode.key}`)}</b>
                      {` — ${t(`tournamentModeNote_${mode.key}`)}`}
                      {!mode.available && ` (${t('tournamentNotYetAvailable')})`}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* ---- HOME AND AWAY ----
                ★ `TournamentStage.Legs` has been stored and validated since the table was made
                and read by NOTHING. A round robin is its first reader, which is why the control
                appears now rather than when the column did — a toggle nothing consults reads as a
                decision that has been taken. Saved on the spot, like the format chips: it changes
                the shape of the whole competition, not one number in a grid. */}
            {usesLegs && (
              <div className="ct-legs">
                <span className="ct-field">
                  <label htmlFor="ct-legs">{t('tournamentLegs')}</label>
                  <select id="ct-legs" value={stage.legs || 1} disabled={locked}
                          onChange={(e) => save({ legs: Number(e.target.value) })}>
                    <option value={1}>{t('tournamentLegs_1')}</option>
                    <option value={2}>{t('tournamentLegs_2')}</option>
                  </select>
                  <span className="ct-field__hint">{t('tournamentLegsHint')}</span>
                </span>
              </div>
            )}

            {/* ---- WHAT THE FORMAT ITSELF ASKS ----
                ★★ Rendered from the format's `stage_fields`, not from its name. These four used
                to be a `format === 'knockout'` test around two of them, which was one of the
                places the platform's single play formula was smeared over — and the reason a
                second formula could never be added without editing this file too. Pools reads all
                four; a knockout reads the last two; heats and a rotation read none and the block
                does not render.

                ★ Saved on the spot, like the format chips and unlike the number grid below: each
                one changes the SHAPE of the competition rather than one figure in it, and all four
                are asked BEFORE anything is generated — a bracket that is half played is half true
                and cannot be redrawn.

                ★ A dropdown rather than a box for the two numbers, on purpose. They have a handful
                of sensible answers, and it means there is nothing to press Save on: a number box
                that saved on every keystroke would write six pools on the way to typing sixteen. */}
            {stageFields.length > 0 && (
              <div className="ct-knockout">
                {stageFields.includes('pool_size') && (
                  <span className="ct-field">
                    <label htmlFor="ct-pool-size">{t('tournamentPoolSize')}</label>
                    <select id="ct-pool-size" value={stage.pool_size || ''} disabled={locked}
                            onChange={(e) => save({
                              pool_size: e.target.value === '' ? null : Number(e.target.value),
                            })}>
                      {/* ★ Blank is a real answer and the default one: "whatever the formula
                          says". Storing the current default instead would pin this event to
                          whatever HQ happened to have set the day it was made. */}
                      <option value="">{t('tournamentPoolSizeDefault')}</option>
                      {[3, 4, 5, 6, 8].map((n) => (
                        <option key={n} value={n}>{t('tournamentPoolSizeOption', { n })}</option>
                      ))}
                    </select>
                    <span className="ct-field__hint">
                      {poolShape
                        ? t('tournamentPoolSizeHint',
                          { pools: poolShape.pools, size: poolShape.pool_size })
                        : t('tournamentPoolSizeEmpty')}
                    </span>
                  </span>
                )}
                {stageFields.includes('advance_count') && (
                  <span className="ct-field">
                    <label htmlFor="ct-advance">{t('tournamentAdvance')}</label>
                    <select id="ct-advance" value={stage.advance_count || ''} disabled={locked}
                            onChange={(e) => save({
                              advance_count: e.target.value === '' ? null : Number(e.target.value),
                            })}>
                      <option value="">{t('tournamentAdvanceDefault')}</option>
                      {[1, 2, 3, 4].map((n) => (
                        <option key={n} value={n}>
                          {/* "Only the winner" and "the best 2" are different sentences, not one
                              sentence with a number in it. Two keys rather than a plural rule the
                              three languages would each break differently. */}
                          {t(n === 1 ? 'tournamentAdvanceOption_1' : 'tournamentAdvanceOption_n',
                            { n })}
                        </option>
                      ))}
                    </select>
                    <span className="ct-field__hint">
                      {poolShape
                        ? t('tournamentAdvanceHint', { n: poolShape.final_entrants })
                        : t('tournamentAdvanceEmpty')}
                    </span>
                  </span>
                )}
                {stageFields.includes('seeding') && (
                  <span className="ct-field">
                    <label htmlFor="ct-seeding">{t('tournamentSeeding')}</label>
                    <select id="ct-seeding" value={stage.seeding} disabled={locked}
                            onChange={(e) => save({ seeding: e.target.value })}>
                      {vocabulary.seedings.map((s) => (
                        <option key={s} value={s}>{t(`tournamentSeeding_${s}`)}</option>
                      ))}
                    </select>
                    <span className="ct-field__hint">
                      {t(poolShape ? 'tournamentSeedingHintPools' : 'tournamentSeedingHint')}
                    </span>
                  </span>
                )}
                {stageFields.includes('third_place') && (
                  <label className="ce-check">
                    <input type="checkbox" checked={stage.third_place} disabled={locked}
                           onChange={(e) => save({ third_place: e.target.checked })} />
                    <span>
                      {t('tournamentThirdPlace')}
                      <span className="ce-check__hint">{t('tournamentThirdPlaceHint')}</span>
                    </span>
                  </label>
                )}
              </div>
            )}

            {/* ---- HOW BIG THE DAY IS ----
                The same event columns the back office edits, so the two can never disagree.

                ★★ ONE day, and on a multi-day event that has to be said out loud. Every number
                under here is per day — the games, the break, the awards, the end time — and an
                organiser reading "6 games" on a two-day event otherwise has to guess whether
                that is the whole festival or a day of it. The guess they make is to halve their
                games and type the half, which puts the stored row out of step with the event's
                own dates. So the sentence comes FIRST, above the numbers it governs. */}
            {days.count > 1 && (
              <p className="ct-multiday">
                <Icon name="calendar" className="me-1" />
                {t('tournamentMultiDay', { days: days.count })}
              </p>
            )}
            <Title my="1" className={days.count > 1 ? 'mt-1' : 'mt-3'}>
              <Icon name="settings" theme="primary" className="me-2" />
              {t(days.count > 1 ? 'tournamentDimensionsPerDay' : 'tournamentDimensions')}
            </Title>
            <div className="ct-grid">
              <div className="ct-col">
                <p className="ct-pick__label">{t('tournamentSizeBlock')}</p>
                {/* The share of the company sits directly under Players, because it is that
                    box being answered — not a row of its own further down. */}
                {SIZE_FIELDS.flatMap((field) => [num(field, {
                  min: 1,
                  max: field === 'amount_of_groups' ? 4 : undefined,
                  // A derived box says so, and says what it follows — otherwise a number that
                  // rewrites itself while you are filling the form reads as the screen fighting
                  // you rather than helping.
                  hint: DERIVED_FROM[field] ? `dim_${field}Derived`
                    : (field === 'amount_of_stations' ? 'dim_stationsHint'
                      : (field === 'amount_of_groups' && noGroups ? 'dim_groupsNone'
                        : (field === 'amount_of_challenges' && oneGame
                          ? 'dim_challengesOne' : null))),
                })].concat(field === 'amount_of_players' && members > 0
                  ? [memberShare()] : []))}
              </div>
              <div className="ct-col">
                <p className="ct-pick__label">{t('tournamentPlanningBlock')}</p>
                {num('expected_start_time', { type: 'time' })}
                {TIME_FIELDS.map((field) => num(field, {
                  unit: true,
                  matchLabel: field === 'challenge_time' ? 'dim_challenge_timeMatch' : null,
                  hint: (field === 'challenge_break_time' && breakFollowsGame
                    ? 'dim_breakFollowsGame' : null),
                }))}
                <span className="ct-field">
                  <label htmlFor="ct-end">{t('dim_expected_end_time')}</label>
                  <input id="ct-end" type="time" readOnly disabled
                         value={preview?.expected_end_time || ''} />
                  <span className="ct-field__hint">{t('dim_endHint')}</span>
                </span>
              </div>
            </div>

            {/* The whole point of showing this to an organiser: the day, in one line. */}
            {preview && (
              <p className="ct-derived">
                <Icon name="clock" className="me-1" />
                {/* ★ Which line to print follows from whether the format HAS a countable
                    pairing, not from which format it is: `matches` comes back null where the
                    whole field is on the pitch at once and "19 matches" would be invented. */}
                {/* ★★ A pools day is two competitions end to end and "7 rounds" says neither of
                    them. The pools and the round they feed are the two numbers an organiser is
                    actually deciding between, so they get their own line. */}
                {poolShape
                  ? t('tournamentShapePools', {
                    pools: poolShape.pools,
                    size: poolShape.pool_size,
                    poolMatches: poolShape.pool_matches,
                    entrants: poolShape.final_entrants,
                    finalMatches: poolShape.final_matches,
                    blocks: preview.blocks,
                    courts: preview.courts,
                  })
                  : isKnockout
                  ? t('tournamentShapeKnockout', {
                    rounds: preview.rounds,
                    matches: preview.matches,
                    byes: preview.byes,
                    blocks: preview.blocks,
                    courts: preview.courts,
                  })
                  : (preview.matches != null
                    ? t('tournamentShapeMatches', {
                      rounds: preview.rounds,
                      matches: preview.matches,
                      blocks: preview.blocks,
                      courts: preview.courts,
                    })
                    : t('tournamentShapeRounds', {
                      rounds: preview.rounds,
                      heat: preview.teams_per_heat,
                    }))}
                {/* ⚠️ Not on a pools day: `byes` there are the walk-throughs of the FINAL bracket,
                    not competitors sitting a round out, and the pools line above already carries
                    them in its own words. */}
                {!isKnockout && !poolShape && preview.matches != null && preview.byes > 0
                  && ` · ${t('tournamentShapeSitOut', { n: preview.byes })}`}
                {' · '}
                {t('tournamentShapeLength', {
                  hours: Math.floor(preview.duration / 60),
                  minutes: preview.duration % 60,
                })}
              </p>
            )}
            {/* ★★ The format's own concern about THIS set-up, and the reason it is a list of
                words rather than a sentence: only the format knows what to look for. A round robin
                grows with the SQUARE of its field where every other formula here grows in a
                straight line, so the day that stops fitting arrives with no warning — and an
                organiser's intuition is calibrated on the straight-line ones. The threshold is
                HQ's, on the format's own admin page. */}
            {(preview?.notes || []).map((note) => (
              <p className="ce-note ce-note--warn" key={note}>{t(`tournamentNote_${note}`)}</p>
            ))}
            {preview?.past_midnight && (
              <p className="ce-note ce-note--warn">{t('tournamentPastMidnight')}</p>
            )}

            {/* ---- HOW WILL IT LOOK ----
                ★★ The numbers above are a set-up nobody can picture. "6 games of 15 minutes, a
                break of 10, an extra break of 60" is arithmetic; what an organiser is actually
                deciding is whether lunch falls at half twelve and whether the prizes are handed
                out before people have to leave — and they cannot see that from the boxes. So the
                same minutes are laid out in the order they are lived, behind a toggle, exactly
                like "what do these mean" is: teaching that is there when it is wanted.

                ★ Built by the SERVER, off the same sum as the end time (``run_sheet``). A
                timetable drawn in JavaScript would be a second opinion about the same day, and
                the two would disagree the first time a rule changed — the group break is exactly
                such a rule. The last row ends at the "Ends at" box above, always. */}
            {preview && (
              <>
                <button type="button" className="ct-explain__toggle"
                        onClick={() => setRunSheet((v) => !v)}>
                  <Icon name={runSheet ? 'angle-left' : 'angle-right'} className="me-1" />
                  {t('tournamentHowWillItLook')}
                </button>
                {runSheet && (
                  <div className="ct-run">
                    {days.count > 1 && (
                      <p className="ct-field__hint">
                        {t('tournamentRunEachDay', { days: days.count })}
                      </p>
                    )}
                    {preview.schedule && preview.schedule.length > 0 ? (
                      <>
                        <ol className="ct-run__list">
                          {preview.schedule.map((row, i) => (
                            <li key={`${row.kind}-${row.index || 0}-${i}`}
                                className={`ct-run__row ct-run__row--${row.kind}`}>
                              <span className="ct-run__clock">
                                {row.start ? `${row.start} – ${row.end}` : '—'}
                              </span>
                              <span className="ct-run__what">{runLabel(t, row, breakFollowsGame)}</span>
                              <span className="ct-run__min">
                                {`${row.minutes} ${t('dim_unitMinutes')}`}
                              </span>
                            </li>
                          ))}
                        </ol>
                        {/* Without a start time it is still a plan, just not a clock. */}
                        {!preview.schedule[0].start && (
                          <p className="ct-field__hint">{t('tournamentRunNoStart')}</p>
                        )}
                        <p className="ct-field__hint">{t('tournamentRunHint')}</p>
                        {/* ★★ From a picture of the day to the day itself. Everything above is
                            arithmetic that is recomputed on every keystroke and stored nowhere;
                            pressing this writes it down as real run-sheet lines, with the playing
                            moments ticked. After that the run sheet is the truth and the editor
                            takes over — which is what makes it possible to put a keynote between
                            two games, the one thing this picture can never show.

                            ★ Only while the run sheet is still empty. This is a starting point,
                            not a merge: a second press would lay a second copy of the day over
                            the first and nothing afterwards could tell them apart. */}
                        {canManage && preview.schedule[0].start
                          && !(data.schedule || {}).rows && (
                          <div className="ce-actions">
                            <button type="button" className="bravos-submit bravos-submit--ghost"
                                    disabled={busy} onClick={seedRunSheet}>
                              <Icon name="calendar" /> {t('tournamentSeedRunSheet')}
                            </button>
                            <p className="ct-field__hint">{t('tournamentSeedRunSheetHint')}</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="ct-field__hint">{t('tournamentRunEmpty')}</p>
                    )}
                    {/* The same day across the FIELD instead of across the clock. Under the one
                        toggle, because "how will it look" is one question. */}
                    <PlanGrid grid={preview.grid} />
                  </div>
                )}
              </>
            )}

            {canManage && (
              <div className="ce-actions">
                <button type="button" className="bravos-submit" disabled={busy || !dirty}
                        onClick={() => save({ dimensions: dims })}>
                  {busy ? t('pleaseWait') : t('save')}
                </button>
              </div>
            )}

            {/* ---- THE FIELD ----
                One line and a door, the same shape as the games at the top of the screen.
                Making twelve teams and filling them is a screen of its own; what belongs here
                is whether it has been done. */}
            <Title my="1" className="mt-3">
              <Icon name="users" theme="primary" className="me-2" />
              {t('tournamentTheField')}
            </Title>
            <p className="ct-sum">
              {teams?.count
                ? t('tournamentFieldSummary', {
                  teams: teams.count,
                  seated: teams.seated,
                  without: teams.without_captain,
                })
                : t('tournamentFieldEmpty')}
            </p>
            <div className="ce-actions">
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => navigate(`/company-event/${eventId}/teams`)}>
                <Icon name="users" /> {t('tournamentGoToTeams')}
              </button>
            </div>

            {/* ---- RUNNING THE DAY ----
                ★★ Everything above describes a competition; this is where it becomes one that can
                be played. Three steps in the order they have to happen — the run sheet says WHEN,
                the ladder says what a place is WORTH, and the schedule says who plays whom — and
                each one names what is still missing rather than leaving a button that does
                nothing.

                ★★ That naming is the whole of this block. The builder underneath is the one
                Hercules Trophy has run on for twenty-five years, and it raises nothing at all when
                there is no run sheet, no team or no game: it returns zeros and the day stays
                empty. So the reasons come back from the server (`schedule_blocked`) and are
                printed here as sentences, exactly like the bracket screen does. */}
            <Title my="1" className="mt-3">
              <Icon name="calendar" theme="primary" className="me-2" />
              {t('tournamentRunTheDay')}
            </Title>

            {/* -- the run sheet, and how many playing moments are on it -- */}
            <p className="ct-pick__label">{t('tournamentRunSheet')}</p>
            <p className="ct-sum">
              {sched.rows
                ? t('tournamentRunSheetSummary', { rows: sched.rows, moments: sched.moments })
                : t('tournamentRunSheetEmpty')}
            </p>
            {sched.rows > 0 && sched.needed > 0 && sched.moments !== sched.needed && (
              <p className="ce-note ce-note--warn">
                {t('tournamentMomentsMismatch',
                  { moments: sched.moments, needed: sched.needed })}
              </p>
            )}
            <div className="ce-actions">
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => navigate(`/host-event/${eventId}/run-sheet`)}>
                <Icon name="calendar" /> {t('tournamentGoToRunSheet')}
              </button>
            </div>

            {/* -- the points ladder: what a place is worth on this day --
                ★★ Typable, not just generatable. "Make me one" hands out Hercules' own curve, which
                is a fine starting point and somebody else's answer: a customer with five teams in a
                heat and a prize for third wants their own numbers. Three columns, and the two
                beside the first are the ones nobody would guess: the JOKER is what the place is
                worth to the team that put its joker on this game, and LADIES is the bonus for a
                women's line-up. */}
            <p className="ct-pick__label mt-3">{t('tournamentLadder')}</p>
            <p className="ct-sum">
              {ladder.places
                ? t('tournamentLadderSummary', { places: ladder.places, top: ladder.top })
                : t('tournamentLadderEmpty')}
            </p>
            {ladderRows.length > 0 && (
              <div className="ct-plan__scroll">
                <table className="ct-plan__table ct-ladder">
                  <thead>
                    <tr>
                      <th>{t('tournamentLadderRank')}</th>
                      <th>{t('tournamentLadderPoints')}</th>
                      <th>{t('tournamentLadderJoker')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ladderRows.map((row) => (
                      <tr key={row.id}>
                        {/* De rang zelf staat vast — hoeveel plaatsen een dag betaalt, is hoe breed
                            een heat is. Machina's scherm zet dit vakje om dezelfde reden op
                            disabled. */}
                        <th scope="row">{t('tournamentLadderPlace', { n: row.rank })}</th>
                        {['score', 'joker'].map((field) => (
                          <td key={field}>
                            <input type="number" min="0" max="999" className="ct-ladder__in"
                                   disabled={locked} value={row[field]}
                                   onChange={(e) => setLadderCell(row.id, field, e.target.value)} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {canManage && ladderRows.length > 0 && (
              <div className="ce-actions">
                <button type="button" className="bravos-submit" disabled={busy || !ladderDirty}
                        onClick={saveLadder}>
                  {busy ? t('pleaseWait') : t('save')}
                </button>
                <p className="ct-field__hint">
                  {sched.scored ? t('tournamentLadderScoredHint') : t('tournamentLadderHint')}
                </p>
              </div>
            )}
            {canManage && !ladder.places && (
              <div className="ce-actions">
                <button type="button" className="bravos-submit bravos-submit--ghost"
                        disabled={busy || !dims.teams_per_challenge}
                        onClick={() => step('POST', '/ladder', {}).catch(() => {})}>
                  <Icon name="list-check" /> {t('tournamentMakeLadder')}
                </button>
                <p className="ct-field__hint">{t('tournamentMakeLadderHint')}</p>
              </div>
            )}

            {/* -- the joker: one game, picked by the team, before it is played --
                ★★ Three ways this is half set up and every one of them is silent — no game allows
                one, a game allows one but the teams cannot see it, or the ladder pays nothing for
                it. The counts come from the server (`_joker_summary`); what is missing is a
                sentence, not a number. */}
            {joker && (
              <>
                <p className="ct-pick__label mt-3">{t('tournamentJoker')}</p>
                <p className="ct-sum">
                  {joker.games
                    ? t('tournamentJokerSummary',
                      { games: joker.games, picked: joker.picked, teams: joker.teams })
                    : t('tournamentJokerNoGames')}
                </p>
                {joker.games > 0 && joker.hidden > 0 && (
                  <p className="ce-note ce-note--warn">
                    {t('tournamentJokerHidden', { n: joker.hidden })}
                  </p>
                )}
                {joker.games > 0 && jokerPaysNothing && (
                  <p className="ce-note ce-note--warn">{t('tournamentJokerNoPoints')}</p>
                )}
                <div className="ce-actions">
                  <button type="button" className="bravos-submit bravos-submit--ghost"
                          onClick={() => navigate(`/company-event/${eventId}/games`)}>
                    <Icon name="star" /> {t('tournamentGoToGames')}
                  </button>
                </div>
              </>
            )}

            {/* -- the schedule itself -- */}
            <p className="ct-pick__label mt-3">{t('tournamentSchedule')}</p>
            <p className="ct-sum">
              {sched.heats
                ? t('tournamentScheduleSummary', { heats: sched.heats })
                : t('tournamentScheduleEmpty')}
            </p>
            {/* ★ Only where this screen owns the button. On a match format the single reason is
                "that door is the bracket's", and the bracket block below already IS that door —
                printing it as a warning would read as something being wrong. */}
            {buildsHere && blocked.length > 0 && !sched.heats && (
              <div className="ce-note ce-note--warn">
                <p className="mb-1">{t('tournamentScheduleNotYet')}</p>
                <ul className="mb-0">
                  {blocked.map((code) => <li key={code}>{t(code)}</li>)}
                </ul>
              </div>
            )}
            {canManage && (
              <div className="ce-actions">
                {!sched.heats && buildsHere && (
                  <button type="button" className="bravos-submit"
                          disabled={busy || blocked.length > 0}
                          onClick={() => step('POST', '/schedule', {}).catch(() => {})}>
                    <Icon name="refresh" /> {t('tournamentGenerate')}
                  </button>
                )}
                {sched.heats > 0 && (
                  <button type="button" className="bravos-submit bravos-submit--ghost"
                          onClick={() => navigate(`/company-event/${eventId}/scoring`)}>
                    <Icon name="star" /> {t('tournamentGoToScoring')}
                  </button>
                )}
                {sched.heats > 0 && (
                  <button type="button" className="bravos-submit bravos-submit--ghost"
                          onClick={() => navigate(`/company-event/${eventId}/ranking`)}>
                    <Icon name="leaderboard" /> {t('tournamentGoToRanking')}
                  </button>
                )}
                {/* ★ Beside the ranking, because the cup IS what the ranking becomes — and on
                    the SAME condition, "something has been played".
                    ⚠️ Deliberately NOT in the houses block above: that one only draws once
                    houses are ON the day, and the cup needs no such row — it reads the standing
                    memberships of the players. Put there, the way in was invisible exactly when
                    an admin most needs it, which is before the day is set up. */}
                {sched.heats > 0 && (
                  <button type="button" className="bravos-submit bravos-submit--ghost"
                          onClick={() => navigate(`/company-event/${eventId}/house-cup`)}>
                    <Icon name="trophy" /> {t('tournamentGoToCup')}
                  </button>
                )}
              </div>
            )}

            {/* -- how much of the board the field may see --
                ★★ Held back on purpose is the Hercules way round: the board opens moment by moment
                and the ceremony is the product. But the column is NULL on almost every event on
                record, and NULL means "show nothing until the day is over" — which during a day
                being played reads as a broken board rather than a held-back one. So it is a dial,
                on this screen, with what it means written next to it. */}
            {sched.heats > 0 && canManage && (
              <>
                <p className="ct-pick__label mt-3">{t('tournamentReveal')}</p>
                <span className="ct-field">
                  <label htmlFor="ct-reveal">{t('tournamentRevealUpTo')}</label>
                  <select id="ct-reveal" disabled={busy}
                          value={data.leaderboard_quarter == null ? ''
                            : String(data.leaderboard_quarter)}
                          onChange={(e) => step('PUT', '/reveal', {
                            leaderboard_quarter: e.target.value === '' ? null
                              : Number(e.target.value),
                          }).catch(() => {})}>
                    <option value="">{t('tournamentRevealNone')}</option>
                    {Array.from({ length: Math.max(sched.moments, sched.needed, 1) },
                      (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>{t('tournamentRevealMoment', { n })}</option>
                    ))}
                  </select>
                  <span className="ct-field__hint">{t('tournamentRevealHint')}</span>
                </span>
              </>
            )}

            {/* Taking a played day back down. Its own button, and deliberately at the bottom:
                generating ALREADY does this silently, which is why generating is refused once
                anything is scored. Throwing away what happened has to be something somebody
                chose. */}
            {canManage && sched.heats > 0 && (
              <div className="kb-danger mt-3">
                <p className="ct-field__hint">{t('tournamentClearScheduleHint')}</p>
                <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy}
                        onClick={() => {
                          // eslint-disable-next-line no-alert
                          if (window.confirm(t('tournamentClearScheduleConfirm'))) {
                            step('DELETE', '/schedule', {}).then(() => load()).catch(() => {});
                          }
                        }}>
                  {t('tournamentClearSchedule')}
                </button>
              </div>
            )}

            {/* ---- THE FIXTURES ----
                ★ The door to the screen where the competition is PLAYED, and it asks the REGISTRY
                rather than testing for the word "knockout". That test lived in six places on this
                screen before the formats package existed, and putting a seventh one here would be
                the whole point of that package undone.

                ⚠️⚠️ On the ENGINE and not on `can_generate`. That was the right test while a
                bracket was the only thing that could be generated at all; the day the rotation and
                heats got generators of their own it started offering a Hercules Trophy a door to
                the bracket screen, which would have met it with "this is not a formula that draws
                its own fixtures". A planning format builds its day on the cockpit — the button is
                further up this very screen.

                ★ The door says which of the two things is behind it. Before the draw it is a
                set-up step ("draw the fixtures"); after it, it is the day itself. Naming it the
                same both times would make an organiser mid-tournament hunt for the results. */}
            {formatInfo.engine === 'match' && stage.can_generate && (
              <>
                <Title my="1" className="mt-3">
                  <Icon name="trophy" theme="primary" className="me-2" />
                  {t(isTable ? 'fixturesTitle' : 'bracketTitle')}
                </Title>
                <p className="ct-sum">
                  {stage.status === 'draft' ? t('tournamentBracketNotDrawn')
                    : (stage.status === 'closed' ? t('tournamentBracketDone')
                      : t('tournamentBracketRunning'))}
                </p>
                <div className="ce-actions">
                  <button type="button" className="bravos-submit bravos-submit--ghost"
                          onClick={() => navigate(`/company-event/${eventId}/bracket`)}>
                    <Icon name="trophy" />{' '}
                    {t(stage.status === 'draft' ? 'tournamentGoToDraw'
                      : 'tournamentGoToBracket')}
                  </button>
                </div>
              </>
            )}

            {/* ★ The set-up on paper. Not the pack — that is the run screen's, for the day itself.
                This is the one an organiser sends ahead to a venue or a supplier: what is being
                played, how big the day is, when it ends, and what the field will look like. */}
            <div className="ce-actions mt-3">
              <button type="button" className="btn btn-outline-secondary btn-sm"
                      onClick={() => downloadApiFile(
                        `/company/event/${eventId}/print/competition.pdf`,
                        { lang: i18n.language })}>
                <Icon name="print" className="me-1" />{t('printSetupButton')}
              </button>
            </div>

            {canManage && stage.can_change_format && (
              <div className="ce-actions mt-3">
                <button type="button" className="bravos-member__makeadmin" disabled={busy}
                        onClick={stop}>
                  {t('tournamentStop')}
                </button>
              </div>
            )}
            <p className="ce-check__hint mt-2">{t('tournamentNextStep')}</p>
          </>
        )}
      </Card>
    </Base>
  );
};

export default CompanyEventTournament;
